import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, CurrencyCircleDollar } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { gameSystems, addCoins, spendCoins, getTotalInBaseUnits, getSymbol } from '../data/gameSystems';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';

export const TransactionModal = ({ isOpen, onClose, type, character }) => {
  const { dispatch } = useApp();
  const { t, lang } = useTranslation();
  const [amount, setAmount] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [note, setNote] = useState('');
  
  const system = gameSystems[character?.systemId];
  const isSpending = type === 'spend';
  
  const handleSubmit = () => {
    const numAmount = parseInt(amount, 10);
    if (!numAmount || numAmount <= 0 || !currencyId) return;
    
    let newWallet;
    
    if (isSpending) {
      const result = spendCoins(character.wallet, numAmount, currencyId, character.systemId);
      if (!result.success) {
        toast.error(t('insufficientFunds'));
        return;
      }
      newWallet = result.wallet;
    } else {
      newWallet = addCoins(character.wallet, numAmount, currencyId);
    }
    
    dispatch({
      type: 'UPDATE_WALLET',
      payload: { characterId: character.id, wallet: newWallet }
    });
    
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        characterId: character.id,
        transaction: {
          type: isSpending ? 'spend' : 'add',
          amount: numAmount,
          currencyId,
          note: note.trim() || null,
        }
      }
    });
    
    toast.success(t('transactionSuccess'));
    handleClose();
  };
  
  const handleClose = () => {
    setAmount('');
    setCurrencyId('');
    setNote('');
    onClose();
  };
  
  if (!character || !system) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          
          {/* Modal */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-3xl p-6 max-w-md mx-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  isSpending ? 'bg-red-500/10' : 'bg-green-500/10'
                }`}>
                  {isSpending ? (
                    <Minus size={24} weight="bold" className="text-red-500" />
                  ) : (
                    <Plus size={24} weight="bold" className="text-green-500" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-[var(--text)]">
                  {isSpending ? t('spendCoins') : t('addCoins')}
                </h2>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[var(--background)] transition-colors"
                data-testid="close-transaction-modal"
              >
                <X size={24} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('amount')}
                </label>
                <Input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="text-2xl font-bold h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                  data-testid="transaction-amount-input"
                />
              </div>
              
              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('coinType')}
                </label>
                <Select value={currencyId} onValueChange={setCurrencyId}>
                  <SelectTrigger 
                    className="h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                    data-testid="currency-select-trigger"
                  >
                    <SelectValue placeholder={t('coinType')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                    {system.currencies.map(currency => (
                      <SelectItem 
                        key={currency.id} 
                        value={currency.id}
                        className="text-[var(--text)]"
                        data-testid={`currency-option-${currency.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <CurrencyCircleDollar size={20} weight="fill" />
                          {lang === 'it' ? currency.nameIt : currency.name} ({getSymbol(currency, lang)})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Note (only for spending) */}
              {isSpending && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                    {t('addNote')}
                  </label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('note')}
                    className="bg-[var(--background)] border-[var(--border)] text-[var(--text)] resize-none"
                    rows={2}
                    data-testid="transaction-note-input"
                  />
                </div>
              )}
              
              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!amount || parseInt(amount, 10) <= 0 || !currencyId}
                className={`w-full h-14 rounded-2xl text-lg font-semibold transition-all ${
                  isSpending 
                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
                data-testid="confirm-transaction-btn"
              >
                {isSpending ? t('spend') : t('add')}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
